<script setup>
import ListingDetails from '@/components/Listing/ListingDetails.vue'
import ListingForm from '@/components/Listing/ListingForm.vue'
import ListingTable from '@/components/dashboard/ListingTable.vue'
import { useAuthStore } from '@/stores/auth'
import { $api } from '@/utils/api'
import { computed, ref, watch } from 'vue'

definePage({
  meta: {
    public: true,
  },
})

const authStore = useAuthStore()
const listingPickerOpen = ref(false)
const selectedListing = ref(null)
const pickerSelectedListingIds = ref([])
const detailsListingLive = ref(null)
const isLoadingListing = ref(false)
const listingLoadError = ref('')
const fixtureLoadError = ref('')
const selectedFixtureKey = ref('')

const isAuthenticated = computed(() => authStore.isAuthenticated)

const fixtureIndexModule = import.meta.glob('../../data/demo/listing-form-details/index.json', { eager: true })
const fixtureCaseModules = import.meta.glob('../../data/demo/listing-form-details/cases/*.json', { eager: true })

const unwrapJsonModule = module => module?.default ?? module ?? null

const deepClone = value => {
  if (value == null)
    return null

  return JSON.parse(JSON.stringify(value))
}

const toLocalDateTimeInput = value => {
  if (!value)
    return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime()))
    return null

  const pad = number => String(number).padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const mapListingToFormData = listing => {
  const payload = listing?.data ?? listing ?? {}
  const specialistAreas = Array.isArray(payload.specialist_areas) ? payload.specialist_areas : []

  return {
    ...payload,
    surgeon_id: payload.surgeon_id ?? payload.surgeon?.id ?? null,
    hospital_id: payload.hospital_id ?? payload.hospital?.id ?? null,
    specialist_area_ids: Array.isArray(payload.specialist_area_ids)
      ? payload.specialist_area_ids
      : specialistAreas.map(area => area?.id).filter(id => id != null),
    scheduled_at: toLocalDateTimeInput(payload.scheduled_at),
    target_anaesthetists: Array.isArray(payload.target_anaesthetists) ? payload.target_anaesthetists : [],
    package_line_items: Array.isArray(payload.package_line_items) ? payload.package_line_items : [],
    anesthetist_ids: Array.isArray(payload.anesthetist_ids)
      ? payload.anesthetist_ids
      : (Array.isArray(payload.target_anaesthetists) ? payload.target_anaesthetists.map(item => item.id).filter(Boolean) : []),
  }
}

const normalizeAreaName = area => area?.name ?? (area?.id != null ? `Area #${area.id}` : 'Area')
const normalizeSurgeonName = surgeon => surgeon?.full_name ?? (surgeon?.id != null ? `Surgeon #${surgeon.id}` : 'Surgeon')
const normalizeHospitalName = hospital => hospital?.name ?? (hospital?.id != null ? `Hospital #${hospital.id}` : 'Hospital')

const formInitialData = computed(() => mapListingToFormData(selectedListing.value))

const availableAnaesthetists = computed(() => {
  const targets = selectedListing.value?.target_anaesthetists
  if (!Array.isArray(targets))
    return []

  return targets.map(item => ({
    id: item.id,
    full_name: item.full_name ?? `Anaesthetist #${item.id}`,
    gmc_number: item.gmc_number ?? item.personal_data?.gmc_number ?? null,
  }))
})

const applyPayloadToDetails = payload => {
  if (!detailsListingLive.value)
    return

  const next = deepClone(detailsListingLive.value)
  const anaesthetistMap = new Map((availableAnaesthetists.value ?? []).map(item => [item.id, item]))

  next.visibility = payload.visibility ?? next.visibility
  next.scheduled_at = payload.scheduled_at ?? next.scheduled_at

  if (payload.surgeon_id != null) {
    if (!next.surgeon)
      next.surgeon = {}

    next.surgeon.id = payload.surgeon_id
    next.surgeon.full_name = normalizeSurgeonName(next.surgeon)
  }

  if (payload.hospital_id != null) {
    if (!next.hospital)
      next.hospital = {}

    next.hospital.id = payload.hospital_id
    next.hospital.name = normalizeHospitalName(next.hospital)
  }

  if (Array.isArray(payload.specialist_area_ids)) {
    const existingById = new Map((next.specialist_areas ?? []).filter(area => area?.id != null).map(area => [area.id, area]))

    next.specialist_areas = payload.specialist_area_ids
      .map(id => {
        const existing = existingById.get(id)
        if (existing)
          return { ...existing, name: normalizeAreaName(existing) }

        return { id, name: `Area #${id}` }
      })
  }

  if (Array.isArray(payload.package_line_items)) {
    next.package_line_items = payload.package_line_items.map(item => ({
      ...item,
      ccsd_code_id: item?.ccsd_code_id ?? item?.ccsd_code?.code ?? item?.ccsd_code?.id,
      ccsd_code: item?.ccsd_code ?? (item?.ccsd_code_id ? { code: String(item.ccsd_code_id) } : null),
    }))
  }

  const selectedIds = Array.isArray(payload.anesthetist_ids) ? payload.anesthetist_ids : []

  next.target_anaesthetists = selectedIds.map(id => {
    const source = anaesthetistMap.get(id)

    return {
      id,
      full_name: source?.full_name ?? source?.personal_data?.full_name ?? `Anaesthetist #${id}`,
    }
  })

  detailsListingLive.value = next
}

const loadListingById = async listingId => {
  if (!listingId)
    return

  listingLoadError.value = ''
  isLoadingListing.value = true

  try {
    const [editResponse, showResponse] = await Promise.all([
      $api(`/api/listings/${listingId}/edit`, { method: 'GET' }),
      $api(`/api/listings/${listingId}`, { method: 'GET' }),
    ])

    const editPayload = editResponse?.data ?? editResponse
    const showPayload = showResponse?.data ?? showResponse

    selectedListing.value = editPayload
    detailsListingLive.value = deepClone(showPayload)
  }
  catch (error) {
    listingLoadError.value = 'Impossibile caricare il listing selezionato.'
    console.error('Failed to load listing for demo page', error)
  }
  finally {
    isLoadingListing.value = false
  }
}

const handlePickerSelectionConfirm = async ids => {
  const listingId = Array.isArray(ids) ? ids[0] : null
  if (!listingId)
    return

  await loadListingById(listingId)
  pickerSelectedListingIds.value = []
  listingPickerOpen.value = false
}

const handlePickerSelectionCancel = () => {
  pickerSelectedListingIds.value = []
}

const selectedListingId = computed(() => selectedListing.value?.id ?? null)

const fixtureCatalog = computed(() => {
  const indexEntry = Object.values(fixtureIndexModule)[0]
  const indexPayload = unwrapJsonModule(indexEntry)
  const cases = Array.isArray(indexPayload?.cases) ? indexPayload.cases : []

  return cases.map(item => ({
    key: item.key,
    title: item.title || item.key,
    file: item.file,
    listingId: item.listing_id ?? null,
  }))
})

const loadFixtureByKey = fixtureKey => {
  if (!fixtureKey)
    return

  fixtureLoadError.value = ''

  const fixture = fixtureCatalog.value.find(item => item.key === fixtureKey)
  if (!fixture?.file) {
    fixtureLoadError.value = 'Fixture non trovato per il caso selezionato.'

    return
  }

  const modulePath = `../../data/demo/listing-form-details/${fixture.file}`
  const moduleEntry = fixtureCaseModules[modulePath]
  const payload = unwrapJsonModule(moduleEntry)

  if (!payload?.listing) {
    fixtureLoadError.value = `Fixture non disponibile: ${fixture.file}`

    return
  }

  selectedFixtureKey.value = fixture.key
  selectedListing.value = deepClone(payload.listing)
  detailsListingLive.value = deepClone(payload.listing)
}

if (!isAuthenticated.value && fixtureCatalog.value.length > 0)
  loadFixtureByKey(fixtureCatalog.value[0].key)

watch(listingPickerOpen, isOpen => {
  if (!isOpen)
    pickerSelectedListingIds.value = []
})
</script>

<template>
  <VContainer
    fluid
    class="parallel-demo-page"
  >
    <VCard class="mb-4">
      <VCardTitle class="d-flex flex-wrap align-center justify-space-between ga-4">
        <div>
          <div class="text-h6">
            ListingForm + ListingDetails (Live Data)
          </div>
          <div class="text-caption text-medium-emphasis">
            Dati reali del segretario loggato con selezione listing da tabella filtrabile.
          </div>
        </div>

        <div class="d-flex ga-2 flex-wrap">
          <VBtn
            color="primary"
            :disabled="!isAuthenticated"
            @click="listingPickerOpen = true"
          >
            Seleziona listing
          </VBtn>
          <VBtn
            :disabled="!selectedListingId || isLoadingListing"
            variant="outlined"
            @click="loadListingById(selectedListingId)"
          >
            Ricarica listing
          </VBtn>
        </div>
      </VCardTitle>

      <VCardText>
        <VAlert
          v-if="listingLoadError"
          type="error"
          variant="tonal"
          class="mb-3"
        >
          {{ listingLoadError }}
        </VAlert>

        <VAlert
          type="info"
          variant="tonal"
        >
          Se non sei autenticato, la pagina usa fixture statici locali. Per rigenerarli lancia:
          <code>php artisan demo:listings:generate-fixtures --overwrite</code>
        </VAlert>

        <VAlert
          v-if="fixtureLoadError"
          type="error"
          variant="tonal"
          class="mt-3"
        >
          {{ fixtureLoadError }}
        </VAlert>

        <VSelect
          v-if="!isAuthenticated"
          v-model="selectedFixtureKey"
          class="mt-3"
          label="Caso demo (fixture offline)"
          :items="fixtureCatalog"
          item-title="title"
          item-value="key"
          density="compact"
          @update:model-value="loadFixtureByKey"
        />

        <div
          v-if="selectedListingId"
          class="text-caption text-medium-emphasis mt-3"
        >
          Listing selezionato: #{{ selectedListingId }}
        </div>
      </VCardText>
    </VCard>

    <VRow class="demo-columns">
      <VCol
        cols="12"
        md="6"
      >
        <VCard class="h-100">
          <VCardTitle>ListingForm</VCardTitle>
          <VCardText class="demo-column-scroll">
            <VProgressLinear
              v-if="isLoadingListing"
              indeterminate
              class="mb-4"
            />

            <VAlert
              v-if="!selectedListing && !isLoadingListing"
              type="warning"
              variant="tonal"
            >
              <span v-if="isAuthenticated">Apri il selettore e scegli un listing reale per iniziare il test.</span>
              <span v-else>Seleziona un caso demo dal menu dei fixture offline.</span>
            </VAlert>

            <ListingForm
              v-else
              :initial-data="formInitialData"
              :available-anaesthetists="availableAnaesthetists"
              @payload-change="applyPayloadToDetails"
            />
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="6"
      >
        <VCard class="h-100">
          <VCardTitle>ListingDetails</VCardTitle>
          <VCardText class="demo-column-scroll">
            <ListingDetails
              :listing="detailsListingLive"
              disable-state-actions
            />
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VDialog
      v-model="listingPickerOpen"
      max-width="1280"
      width="96vw"
      scrollable
    >
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>Seleziona listing reale</span>
          <VBtn
            icon="ri-close-line"
            variant="text"
            @click="listingPickerOpen = false"
          />
        </VCardTitle>
        <VCardSubtitle>
          Usa i filtri reali della tabella e seleziona un solo record, poi conferma.
        </VCardSubtitle>
        <VCardText>
          <ListingTable
            endpoint="/api/secretary/listings"
            selection-mode="single"
            selection-confirm-label="Conferma listing"
            selection-cancel-label="Annulla selezione"
            :selected-listing-ids="pickerSelectedListingIds"
            @update:selected-listing-ids="pickerSelectedListingIds = $event"
            @selection-confirm="handlePickerSelectionConfirm"
            @selection-cancel="handlePickerSelectionCancel"
          />
        </VCardText>
      </VCard>
    </VDialog>
  </VContainer>
</template>

<style scoped>
.parallel-demo-page {
  min-height: calc(100vh - 96px);
}

.demo-columns {
  align-items: stretch;
}

.demo-column-scroll {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}
</style>
